import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  BookOpen,
  CheckCircle,
  Clock,
  Layers,
  User,
  AlertCircle,
} from "lucide-react";
import { useGlobal } from "../../context/GlobalContext";
import { images } from "../../assets/images";

const LearningList = () => {
  const navigate = useNavigate();
  const { courseList, getCourseList } = useGlobal();
  const [activeTab, setActiveTab] = useState(
    () => sessionStorage.getItem("learningTab") || "yourArticles",
  );

  const handleTabChange = (tab) => {
    sessionStorage.setItem("learningTab", tab);
    setActiveTab(tab);
  };
  const [search, setSearch] = useState("");

  useEffect(() => {
    getCourseList(search);
  }, [search, getCourseList]);

  const completedCourses = courseList.filter(
    (c) => c.status?.label?.toLowerCase() === "completed",
  );

  const yourArticles = courseList.filter(
    (c) => c.status?.label?.toLowerCase() !== "completed",
  );
  const filteredList =
    activeTab === "completed" ? completedCourses : yourArticles;

  return (
    <div className="content-part2 position-relative">
      <div className="learning-fixed-top shadow-sm border-bottom">
        <div className="row align-items-center g-3">
          <div className="col-12 col-md-6 col-xxl-8">
            <div className="form-group position-relative w-100">
              <Search
                className="position-absolute text-muted"
                size={18}
                style={{
                  left: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
              <input
                type="text"
                className="form-control border-0 bg-light rounded-pill fs-15 input-tags"
                style={{ paddingLeft: "42px", height: "48px" }}
                placeholder="Search for articles, guides..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="col-12 col-md-6 col-xxl-4">
            <div className="db-tabs d-flex justify-content-md-end gap-2 flex-wrap">
              <button
                className={`db-tab ${activeTab === "yourArticles" ? "active" : ""}`}
                onClick={() => handleTabChange("yourArticles")}
              >
                <BookOpen size={18} />
                Your Articles
                <span className="db-tab-count">{yourArticles.length}</span>
              </button>

              <button
                className={`db-tab ${activeTab === "completed" ? "active" : ""}`}
                onClick={() => handleTabChange("completed")}
              >
                <CheckCircle size={18} />
                Completed
                <span className="db-tab-count">{completedCourses.length}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-2 learning-content p-0">
        {filteredList.length === 0 ? (
          <div className="col-12 text-center py-5 text-muted">
            <BookOpen size={40} className="mb-2 opacity-50" />
            <p className="fs-14">
              {activeTab === "completed"
                ? search.trim()
                  ? `No completed courses found for "${search.trim()}"`
                  : "No completed courses yet"
                : search.trim()
                  ? `No pending courses found for "${search.trim()}"`
                  : "No pending courses"}
            </p>
          </div>
        ) : (
          filteredList.map((item) => (
            <div key={item.course_pid} className="col-6 col-lg-4 col-xl-3">
              <div
                className="rewards-data-card d-flex flex-column h-100"
                role="button"
                tabIndex={0}
                onClick={() =>
                  navigate("/read-learning", {
                    state: { course_pid: item.course_pid },
                  })
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate("/read-learning", {
                      state: { course_pid: item.course_pid },
                    });
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <div
                  className="rewards-content-img position-relative"
                  style={{ height: "140px" }}
                >
                  <img
                    src={item.image_url || images.placeHolderImg}
                    alt={item.title}
                    className="w-100 h-100 object-fit-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = images.placeHolderImg;
                    }}
                  />
                  {item.status === "In Progress" && (
                    <span
                      className="badge position-absolute fs-10 fw-semibold"
                      style={{
                        top: "8px",
                        left: "8px",
                        background: "rgba(255, 152, 0, 0.92)",
                        color: "#fff",
                        borderRadius: "20px",
                        padding: "3px 10px",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <Clock size={10} className="me-1" />
                      In Progress
                    </span>
                  )}
                  {item.category && (
                    <span className="badge glass-card border-0 text-dark position-absolute top-2 start-2 m-2 fs-11 fw-bold">
                      {item.category}
                    </span>
                  )}
                  {item.is_mandatory === "yes" && (
                    <span
                      className="badge position-absolute fs-10 fw-semibold d-inline-flex align-items-center"
                      style={{
                        top: "8px",
                        right: "8px",
                        background: "rgba(220, 38, 38, 0.92)",
                        color: "#fff",
                        borderRadius: "20px",
                        padding: "3px 10px",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <AlertCircle size={10} className="me-1" />
                      Mandatory
                    </span>
                  )}
                </div>

                <div className="rewards-content p-3 d-flex flex-column flex-grow-1">
                  {item.duration && (
                    <div className="d-flex align-items-center gap-1 mb-2 opacity-75">
                      <Clock size={12} />
                      <span className="fs-12 fw-medium">
                        {item.duration} read
                      </span>
                    </div>
                  )}

                  <h6 className="line-clamp-2 fs-14 fw-bold text-dark">
                    {item.course_name || item.title}
                  </h6>

                  {item.short_desc && (
                    <div
                      className="fs-12 mb-2 text-muted line-clamp-2 lms-short-desc"
                      dangerouslySetInnerHTML={{ __html: item.short_desc }}
                    />
                  )}

                  <div className="d-flex align-items-center gap-1 mb-2 opacity-75">
                    <Layers size={12} />
                    <span className="fs-12 fw-medium">
                      {item.module_count > 0
                        ? `${item.module_count} ${item.module_count === 1 ? "Module" : "Modules"}`
                        : "No Modules"}
                    </span>
                  </div>

                  <div className="mt-auto">
                    <hr className="mt-1 text-muted" />

                    <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2">
                      <div className="fs-12 d-flex align-items-center text-muted gap-1 overflow-hidden">
                        <User
                          color="gray"
                          size={14}
                          className="flex-shrink-0"
                        />
                        <span className="text-truncate">{item.author}</span>
                      </div>

                      <button
                        type="button"
                        className="btn btn-primary btn-sm py-1 px-3 text-nowrap flex-shrink-0"
                        tabIndex={-1}
                      >
                        Read More
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LearningList;
